import { Button } from "@mui/material";
import { type FC, useState } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import { useFlag } from "../../../../../server/optimizely";
import { useDialog } from "../../../../../utils/hooks/use-dialog";
import { useAnalytics } from "../../../../analytics/analytics";
import { useAddReviewStep } from "../../../hooks/use-add-review-step";
import type { MomentFormMoment } from "../../../types/moment-form-moment";
import type { SaveButtonProps } from "../types";
import {
  SaveChangesBeforeReviewDialog,
  SendForReviewDialog,
  type SendForReviewSelection,
} from "./send-for-review-dialog";

export const SendForReviewButton: FC<SaveButtonProps> = ({
  formSaveState,
  handleSave,
  saveDataReady,
  ...buttonProps
}) => {
  const saveWarning = useDialog();
  const reviewDialog = useDialog();
  const [sending, setSending] = useState(false);
  const { control, getValues, setValue } = useFormContext<MomentFormMoment>();
  const { isDirty } = useFormState<MomentFormMoment>();
  const { gaEvent } = useAnalytics();
  const addReviewStep = useAddReviewStep();
  const [reviewEnabled] = useFlag("cha-4075-review-moments");
  const { saving, setSaving, formDisabled } = formSaveState;

  useWatch({ control, name: ["status", "program_id"] });
  const { id, status, program_id, reviewer_person_id } = getValues();

  const openReviewFlow = (): void => {
    if (isDirty) saveWarning.handleOpen();
    else reviewDialog.handleOpen();
  };

  const saveAndContinue = async (): Promise<void> => {
    setSaving(true);
    try {
      const result = await handleSave(getValues());

      if (result?.status === "valid") {
        saveWarning.handleClose();
        reviewDialog.handleOpen();
      } else if (result?.status === "invalid") {
        toast.error(result.message);
      } else if (result?.status === "error") {
        toast.error("Something went wrong");
      }
    } finally {
      setSaving(false);
    }
  };

  const sendForReview = async ({ reviewer, message }: SendForReviewSelection): Promise<void> => {
    setSending(true);
    try {
      await addReviewStep({
        moment_id: id,
        review_status: "IN_REVIEW",
        reviewer_person_id: reviewer.id,
        note: message || null,
      });
    } catch {
      toast.error("Couldn't send for review");
      return;
    } finally {
      setSending(false);
    }

    gaEvent("send_for_review", { event_category: "moment" });
    // Keeps the header pill in step without re-reading the moment: the header watches this field.
    setValue("review_status", "IN_REVIEW");
    setValue("reviewer_person_id", reviewer.id);
    reviewDialog.handleClose();
    toast.success("Sent for review");
  };

  if (!reviewEnabled || status !== "draft") return null;

  return (
    <>
      <Button
        variant="outlined"
        {...buttonProps}
        loading={saving}
        disabled={formDisabled["draft-button"] || !saveDataReady || !program_id}
        onClick={openReviewFlow}
        data-analytics-id="moment-form-send-for-review"
      >
        Send for Review
      </Button>
      <SaveChangesBeforeReviewDialog
        dialog={saveWarning}
        saving={saving}
        onSave={() => void saveAndContinue()}
      />
      {program_id ? (
        <SendForReviewDialog
          dialog={reviewDialog}
          programId={program_id}
          currentReviewerId={reviewer_person_id}
          sending={sending}
          onSend={(selection) => void sendForReview(selection)}
        />
      ) : null}
    </>
  );
};
